import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { Asset, ImageMetadata, PanelIntent, PanelView } from '../types';
import { NoOp } from 'utils/noop';
import { PanelPaginationContextProvider } from '../contexts/PanelPaginationContextProvider';
import { UploadAssetsContextProvider } from '../contexts/UploadAssetsContextProvider';
import { PanelContextProvider } from '../contexts/PanelProvider';
import { AllImagesCreativeLibraryPanelContent } from './panel/PanelContent';
import { AssetsAndFoldersCreativeLibraryPanelContent } from './assetsAndFoldersPanel/PanelContent';
import { ViewContextProvider } from '../contexts/ViewContext';
import { PanelSortingContextProvider } from '../contexts/PanelSortingContextProvider';
import { defaults } from '../constants';
import { PanelFilterContextProvider } from '../contexts/PanelFilterContextProvider';
import { AssetsQueryContextProvider } from '../contexts/AssetsQueryContextProvider';
import { CloseModalProvider } from '../contexts/CloseModalOverlayContext';
import { SelectionContextProvider } from '../contexts/SelectionContext';

type Props = {
  initView?: PanelView;
  intent?: PanelIntent;
  onCancel?: NoOp;
  onClose: NoOp;
  onInsert?: (attrs: Record<string, string>) => void;
  onSelect?: (url: string, imgMetadata?: ImageMetadata) => void;
  selectedAssetData?: Asset;
  selectedEditorImageNode?: HTMLImageElement;
  hideGifs?: boolean;
};

export const testIds = {
  panel: 'cl-panel',
  panelFooter: {
    footerBtnCancel: 'cl-panel-detail-footer_btn-cancel',
    footerBtnInsert: 'cl-panel-detail-footer_btn-insert',
    footerBtnSelect: 'cl-panel-detail-footer_btn-select',
  },
};

type CreativeLibraryPanelProvidersProps = {
  initView: PanelView;
  intent: PanelIntent;
  onCancel: NoOp;
  onClose: NoOp;
  onInsert?: (attrs: Record<string, string>) => void;
  onSelect?: (url: string, imgMetadata?: ImageMetadata) => void;
  selectedEditorImageNode?: HTMLImageElement;
  selectedAssetData?: Asset;
  hideGifs: boolean;
  children: React.ReactNode;
};

const AllImagesPanelProviders = ({
  initView,
  intent,
  onCancel,
  onClose,
  onInsert,
  onSelect,
  selectedEditorImageNode,
  selectedAssetData,
  hideGifs,
  children,
}: CreativeLibraryPanelProvidersProps) => {
  return (
    <PanelFilterContextProvider>
      <PanelPaginationContextProvider>
        <PanelContextProvider
          initView={initView}
          intent={intent}
          onCancel={onCancel}
          onClose={onClose}
          onInsert={onInsert}
          onSelect={onSelect}
          selectedEditorImageNode={selectedEditorImageNode}
          hideGifs={hideGifs}
          selectedAssetData={selectedAssetData}
        >
          <AssetsQueryContextProvider append isPanel>
            <UploadAssetsContextProvider isPanel>{children}</UploadAssetsContextProvider>
          </AssetsQueryContextProvider>
        </PanelContextProvider>
      </PanelPaginationContextProvider>
    </PanelFilterContextProvider>
  );
};

const AssetsAndFoldersPanelProviders = ({
  initView,
  intent,
  onCancel,
  onClose,
  onInsert,
  onSelect,
  selectedEditorImageNode,
  hideGifs,
  children,
  selectedAssetData,
}: CreativeLibraryPanelProvidersProps) => {
  return (
    <ViewContextProvider>
      <PanelSortingContextProvider defaultSortField={defaults.sortBy}>
        <PanelFilterContextProvider>
          <PanelPaginationContextProvider>
            <SelectionContextProvider>
              <PanelContextProvider
                initView={initView}
                intent={intent}
                onCancel={onCancel}
                onClose={onClose}
                onInsert={onInsert}
                onSelect={onSelect}
                selectedEditorImageNode={selectedEditorImageNode}
                selectedAssetData={selectedAssetData}
                hideGifs={hideGifs}
              >
                <AssetsQueryContextProvider isPanel>
                  <UploadAssetsContextProvider canEditBeforeUpload isPanel>
                    <CloseModalProvider>{children}</CloseModalProvider>
                  </UploadAssetsContextProvider>
                </AssetsQueryContextProvider>
              </PanelContextProvider>
            </SelectionContextProvider>
          </PanelPaginationContextProvider>
        </PanelFilterContextProvider>
      </PanelSortingContextProvider>
    </ViewContextProvider>
  );
};

export const CreativeLibraryPanel = ({
  initView = PanelView.List,
  intent = PanelIntent.Browse,
  onCancel, // close the panel without select or insert (necessary for drag-and-drop (Bee) - includes additional instructions to terminate the insertion without a result)
  onClose, // close the panel after selection or insertion
  onInsert,
  onSelect,
  selectedEditorImageNode,
  hideGifs = false,
  selectedAssetData,
}: Props) => {
  const isCreativeLibraryAssetsFolderPanelFFEnabled = useFeatureFlag(
    'CreativeLibraryAssetsFolderPanel',
    false,
  );
  const onCancelPanel = onCancel ?? onClose;

  if (isCreativeLibraryAssetsFolderPanelFFEnabled) {
    return (
      <AssetsAndFoldersPanelProviders
        initView={initView}
        intent={intent}
        onCancel={onCancelPanel}
        onClose={onClose}
        onInsert={onInsert}
        onSelect={onSelect}
        selectedEditorImageNode={selectedEditorImageNode}
        hideGifs={hideGifs}
        selectedAssetData={selectedAssetData}
      >
        <AssetsAndFoldersCreativeLibraryPanelContent />
      </AssetsAndFoldersPanelProviders>
    );
  }

  return (
    <AllImagesPanelProviders
      initView={initView}
      intent={intent}
      onCancel={onCancelPanel}
      onClose={onClose}
      onInsert={onInsert}
      onSelect={onSelect}
      selectedEditorImageNode={selectedEditorImageNode}
      hideGifs={hideGifs}
      selectedAssetData={selectedAssetData}
    >
      <AllImagesCreativeLibraryPanelContent />
    </AllImagesPanelProviders>
  );
};
